/**
 * AddUserPage Component
 * Full page for creating a new user with permission assignment
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  UserPlusIcon,
  UserIcon,
  KeyIcon,
  CheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { createUser, getAvailablePermissions, getAvailableProducers } from '../../../services/oathUserService';
import { useOathAuth } from '../../../services/oathAuthContext';
import { toast } from 'react-toastify';
import PermissionSelector from './PermissionSelector';
import AdminTabNavigation from './AdminTabNavigation';
import { formatPhoneNumber, stripPhoneFormatting } from './adminUtils';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathConfirmDialog,
} from '../../shared/OathUI';

const AddUserPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useOathAuth();

  // Get agencyId from URL search params (set by AdminPage) or fallback to first agency
  const agencyId = searchParams.get('agencyId') || currentUser?.agencies?.[0]?.agencyId;
  const fallbackAgency = currentUser?.agencies?.[0];
  const agencyName = location.state?.agencyName ||
    (fallbackAgency ? `${fallbackAgency.dbaAgencyName || fallbackAgency.legalAgencyName} (${fallbackAgency.agencyId})` : null);

  // Get tab from URL, default to 'details'
  const activeTab = searchParams.get('tab') || 'details';
  const setActiveTab = (tab) => {
    const params = { tab };
    if (agencyId) params.agencyId = agencyId;
    setSearchParams(params, { replace: true });
  };
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [availableProducers, setAvailableProducers] = useState([]);
  const [selectedProducerId, setSelectedProducerId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showNoPermsConfirm, setShowNoPermsConfirm] = useState(false);

  // Define tabs with dynamic badge for permissions
  const tabs = [
    { id: 'details', label: 'User Details', icon: UserIcon },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: KeyIcon,
      badge: selectedPermissions.length > 0 ? selectedPermissions.length : null
    },
  ];

  // Fetch available permissions and available producers
  useEffect(() => {
    const fetchData = async () => {
      setPermissionsLoading(true);
      const [permResult, producerResult] = await Promise.all([
        getAvailablePermissions(agencyId),
        getAvailableProducers(agencyId),
      ]);
      if (permResult.success) {
        setAvailablePermissions(permResult.permissions || []);
      }
      if (producerResult.success) {
        setAvailableProducers(producerResult.producers || []);
      }
      setPermissionsLoading(false);
    };

    fetchData();
  }, [agencyId]);

  const handleProducerChange = (producerId) => {
    setSelectedProducerId(producerId);
    if (producerId) {
      const producer = availableProducers.find((p) => String(p.producerId) === producerId);
      if (producer) {
        const first = (producer.firstName || '').trim();
        const last = (producer.lastName || '').trim();
        const username = `${first}.${last}.${producer.producerId}`.toLowerCase().replace(/[^a-z0-9._-]/g, '');
        setFormData((prev) => ({
          ...prev,
          firstName: first,
          lastName: last,
          username,
          email: producer.email || prev.email,
          phoneNumber: producer.phone ? formatPhoneNumber(producer.phone) : (producer.mobile ? formatPhoneNumber(producer.mobile) : prev.phoneNumber),
        }));
        setErrors((prev) => ({ ...prev, firstName: null, lastName: null, username: null, email: null }));
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Auto-format phone number
    if (name === 'phoneNumber') {
      processedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.trim().length > 50) {
      newErrors.username = 'Username must be 50 characters or less';
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]*[a-zA-Z0-9]$/.test(formData.username.trim())) {
      newErrors.username = 'Must start/end with a letter or number. Only letters, numbers, dots, hyphens, and underscores allowed.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setSearchParams({ tab: 'details' }, { replace: true });
      return;
    }

    if (selectedPermissions.length === 0) {
      setShowNoPermsConfirm(true);
      return;
    }

    await submitUser();
  };

  const submitUser = async () => {
    setIsSubmitting(true);

    const userData = {
      username: formData.username.trim(),
      email: formData.email.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: stripPhoneFormatting(formData.phoneNumber) || null,
      permissionIds: selectedPermissions.length > 0 ? selectedPermissions : undefined,
      producerId: selectedProducerId ? parseInt(selectedProducerId, 10) : undefined,
    };

    const result = await createUser(agencyId, userData);

    if (result.success) {
      toast.success('User created — a welcome email with login instructions has been sent');
      handleBack();
    } else {
      toast.error(result.error || 'Failed to create user');
    }

    setIsSubmitting(false);
  };

  const handleBack = () => {
    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    params.set('tab', 'users');
    navigate(`/app/admin?${params.toString()}`);
  };

  return (
    <OathPageWrapper maxWidth="max-w-7xl" responsive>
      {/* Header */}
      <div className="mb-6">
        <OathHeaderCard
          title="Add New User"
          subtitle={agencyName}
          icon={UserPlusIcon}
        />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} autoComplete="off">
        <OathContentCard>
          {/* Tab Navigation */}
          <AdminTabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={tabs}
          />

          {/* Tab Content */}
          {activeTab === 'details' && (
            <div className="space-y-3">
              {/* Row 1: Executing Agent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Executing Agent <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                {availableProducers.length > 0 ? (
                  <>
                    <div className="relative sm:max-w-sm">
                      <IdentificationIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <select
                        value={selectedProducerId}
                        onChange={(e) => handleProducerChange(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white appearance-none"
                      >
                        <option value="">Not an executing agent</option>
                        {availableProducers.map((p) => (
                          <option key={p.producerId} value={p.producerId}>
                            {p.fullName || `${p.firstName} ${p.lastName}`}{p.isPrimary ? ' (Primary)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Once linked, this cannot be changed. Selecting an agent will pre-fill the name and username fields.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
                    No executing agents available to link
                  </p>
                )}
              </div>

              {/* Row 2: Username + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                        errors.username ? 'border-red-300 bg-red-50/50 dark:border-red-500 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="john.doe"
                      autoComplete="new-password"
                      data-1p-ignore
                      data-lpignore="true"
                    />
                  </div>
                  {errors.username ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.username}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Letters, numbers, dots, hyphens, and underscores
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                        errors.email ? 'border-red-300 bg-red-50/50 dark:border-red-500 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="user@example.com"
                      autoComplete="new-password"
                      data-1p-ignore
                      data-lpignore="true"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Row 3: First Name + Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                      errors.firstName ? 'border-red-300 bg-red-50/50 dark:border-red-500 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="First name"
                    autoComplete="new-password"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                      errors.lastName ? 'border-red-300 bg-red-50/50 dark:border-red-500 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Last name"
                    autoComplete="new-password"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Row 4: Phone */}
              <div className="sm:max-w-[calc(50%-0.375rem)]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    placeholder="(555) 555-5555"
                    autoComplete="new-password"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                </div>
              </div>

              {/* Welcome email info */}
              <div className="flex items-start gap-2.5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <EnvelopeIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  A welcome email will be sent to the user with a link to set their password. The link expires in 1 hour.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div>
              <PermissionSelector
                availablePermissions={availablePermissions}
                selectedPermissions={selectedPermissions}
                onChange={setSelectedPermissions}
                loading={permissionsLoading}
              />
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            <OathButton
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={isSubmitting}
              className="order-2 sm:order-1"
            >
              Cancel
            </OathButton>
            <OathButton
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              className="order-1 sm:order-2"
            >
              <CheckIcon className="h-4 w-4" />
              {isSubmitting ? 'Creating...' : 'Create User'}
            </OathButton>
          </div>
        </OathContentCard>
      </form>

      <OathConfirmDialog
        open={showNoPermsConfirm}
        title="No permissions assigned"
        message="This user won't be able to access any features until permissions are granted. Would you like to continue?"
        confirmLabel="Create without permissions"
        cancelLabel="Go back"
        variant="warning"
        onConfirm={() => { setShowNoPermsConfirm(false); submitUser(); }}
        onCancel={() => setShowNoPermsConfirm(false)}
      />
    </OathPageWrapper>
  );
};

export default AddUserPage;
